import * as Yup from 'yup';

import { isRichTextEmpty } from 'components/form/richtext/RichTextInput';

// Source: https://www.zizka.ch/pages/programming/ruzne/rodne-cislo-identifikacni-cislo-rc-ico-kontrola-validace.html#toc-ic-javascript
export const validateIco = (x?: string | null, validIfEmpty = false) => {
	if (!x) {
		return validIfEmpty;
	}

	try {
		let a = 0;
		if (x.length === 0) {
			return validIfEmpty;
		}
		if (x.length !== 8) {
			throw new Error('INVALID_IC_LENGTH');
		}

		const b = x.split('');
		let c = 0;

		for (let i = 0; i < 7; i++) {
			a += parseInt(b[i]) * (8 - i);
		}

		a = a % 11;
		c = 11 - a;

		if (a === 1) {
			c = 0;
		}
		if (a === 0) {
			c = 1;
		}
		if (a === 10) {
			c = 1;
		}

		if (parseInt(b[7]) !== c) {
			throw new Error('INVALID_IC_CONTENT');
		}
	} catch (e) {
		return false;
	}

	return true;
};

// Source: https://www.zizka.ch/pages/programming/ruzne/rodne-cislo-identifikacni-cislo-rc-ico-kontrola-validace.html#toc-rodne-cislo-javascript
export const validateRc = (x: string, xAge: number, validIfEmpty = false) => {
	let age = xAge;

	if (!xAge) {
		age = 0;
	}

	try {
		if (x.length === 0) {
			return validIfEmpty;
		}

		if (x.length < 9) {
			throw new Error('INVALID_RC_LENGTH');
		}

		let year = parseInt(x.substr(0, 2), 10);
		let month = parseInt(x.substr(2, 2), 10);
		const day = parseInt(x.substr(4, 2), 10);

		if (x.length === 9 && year < 54) {
			return true;
		}

		let c = 0;
		if (x.length === 10) {
			c = parseInt(x.substr(9, 1));
		}

		let m = parseInt(x.substr(0, 9)) % 11;
		if (m === 10) {
			m = 0;
		}
		if (m !== c) {
			throw new Error('INVALID_RC_LCONTENT');
		}

		year += year < 54 ? 2000 : 1900;
		if (month > 70 && year > 2003) {
			month -= 70;
		} else if (month > 50) {
			month -= 50;
		} else if (month > 20 && year > 2003) {
			month -= 20;
		}

		const d = new Date();
		if (year + age > d.getFullYear()) {
			throw new Error('INVALID_RC_LCONTENT');
		}
		if (month === 0) {
			throw new Error('INVALID_RC_LCONTENT');
		}
		if (month > 12) {
			throw new Error('INVALID_RC_LCONTENT');
		}
		if (day === 0) {
			throw new Error('INVALID_RC_LCONTENT');
		}
		if (day > 31) {
			throw new Error('INVALID_RC_LCONTENT');
		}
	} catch (e) {
		return false;
	}
	return true;
};

// Source: https://www.zizka.ch/pages/programming/ruzne/rodne-cislo-identifikacni-cislo-rc-ico-kontrola-validace.html#toc-dic-javascript
export const validateDic = (x?: string | null, validIfEmpty = false) => {
	if (!x) {
		return validIfEmpty;
	}

	try {
		if (x.length === 0) {
			return validIfEmpty;
		}

		const id = x.substr(0, 2).toUpperCase();
		x = x.substr(2);

		if (id === 'CZ' || id === 'SK') {
			if (x.length < 8) {
				throw new Error('INVALID_IC_LENGTH');
			} else if (x.length > 11) {
				throw new Error('INVALID_IC_LENGTH');
			} else {
				return true;
			}
		}

		return validateRc(x, 18, validIfEmpty);
	} catch (e) {
		return false;
	}
};

export const passwordValidationSchema = Yup.string().matches(
	/^(?=.*\d)(?=.*[A-Z]).{8,}$/,
	'password',
);

export const phoneValidationSchema = Yup.string().matches(
	/^((00|\+)\d{1,3} ?)?(\d ?){9}$/,
	'phone',
);

export const requiredRichTextSchema = Yup.string().test(
	'requiredRichTextSchema',
	'required',
	value => (value ? !isRichTextEmpty(value) : false),
);

export const shortStringSchema = Yup.string().trim().max(100);

export const mediumStringSchema = Yup.string().trim().max(500);

export const longStringSchema = Yup.string().trim().max(2000);

export const multistringSchema = (
	schema: Yup.StringSchema,
	isRequired = false,
) =>
	isRequired
		? Yup.object().shape(
				{
					CZECH: schema.when('ENGLISH', {
						is: ENGLISH => !ENGLISH || ENGLISH === '',
						then: schema.required(),
						otherwise: schema,
					}),
					ENGLISH: schema.when('CZECH', {
						is: CZECH => !CZECH || CZECH === '',
						then: schema.required(),
						otherwise: schema,
					}),
				},
				[['CZECH', 'ENGLISH']],
		  )
		: Yup.object({
				CZECH: schema,
				ENGLISH: schema,
		  });

export const descriptionPageSchema = Yup.object({
	title: multistringSchema(shortStringSchema, true),
	content: multistringSchema(
		Yup.string().test(
			'richTextMaxCharCount',
			() => ({ args: ['max_string', { max: 4000 }] }),
			value => (value ? value.replace(/<[^>]*>/g, '').length < 4000 : true),
		),
	),
});

export const urlValidationSchema = shortStringSchema.matches(
	/^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/i,
	'url',
);

export const facebookUrlValidationSchema = shortStringSchema.matches(
	/^((http|https):\/\/)?(www[.])?(facebook|fb).com\/.+$/i,
	'url',
);
export const instagramUrlValidationSchema = shortStringSchema.matches(
	/^((http|https):\/\/)?(www[.])?(instagram).com\/.+$/i,
	'url',
);
export const linkedInUrlValidationSchema = shortStringSchema.matches(
	/^((http|https):\/\/)?(www[.])?(linkedin).com\/.+$/i,
	'url',
);
export const twitterUrlValidationSchema = shortStringSchema.matches(
	/^((http|https):\/\/)?(www[.])?(twitter).com\/.+$/i,
	'url',
);

export const dateSchema = Yup.string()
	.nullable()
	.test(
		'range',
		'max_date',
		d => !d || new Date(d).getFullYear() <= new Date().getFullYear(),
	)
	.test('range', 'min_date', d => !d || new Date(d).getFullYear() > 1989);

export const customEducationSchema = Yup.object({
	beginDate: dateSchema.required(),
	endDate: dateSchema,
	studyForm: Yup.object().nullable(),
	studyType: Yup.object().nullable().required(),
	studyBranch: shortStringSchema,
	studyProgramme: shortStringSchema.required(),
	faculty: shortStringSchema.required(),
	university: shortStringSchema.required(),
	language: Yup.object().nullable(),
});

export const workExperienceSchema = Yup.object({
	companyName: shortStringSchema.required(),
	jobTitle: shortStringSchema.required(),
	address: shortStringSchema,
	dateFrom: dateSchema.required(),
	dateTo: dateSchema,
	description: mediumStringSchema.required(),
	correspondentName: shortStringSchema,
	correspondentContact: shortStringSchema,
	businessSectors: Yup.array().of(Yup.object()).nullable().required(),
});

export const graduationThesesSchema = Yup.object({
	multiName: multistringSchema(shortStringSchema, true),
	type: Yup.string().nullable().required(),
	url: shortStringSchema.url(),
});

export const coursesSchema = Yup.object({
	descriptionPage: descriptionPageSchema,
});

// Merge Yup schemas
// Source: https://github.com/jquense/yup/issues/232#issuecomment-499789569

export function mergeSchemas(...schemas: Yup.ObjectSchema[]) {
	const [first, ...rest] = schemas;

	const merged = rest.reduce(
		(mergedSchemas, schema) => mergedSchemas.concat(schema),
		first,
	);

	return merged;
}
